import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import eventsData from '../data/events.json';
import '../styles/Events.css'; // Import the CSS for Events
import foot from '../assets/foot.webp';
import crick from '../assets/crick.webp';
import basket from '../assets/basket.webp';
import valley from '../assets/valley.webp';
import tenis from '../assets/tenis.webp';
import badmin from '../assets/badmin.webp';

function Events() {
  const [events, setEvents] = useState([]);
  const [filteredEvents, setFilteredEvents] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedSport, setSelectedSport] = useState('All');
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [selectedStatus, setSelectedStatus] = useState('All');
  const [currentPage, setCurrentPage] = useState(1);
  const eventsPerPage = 6;

  // Get unique sports for filter
  const sports = ['All', ...new Set(eventsData.events.map(e => e.sport))];
  const categories = ['All', 'Men', 'Women', 'Mixed'];
  const statuses = ['All', 'upcoming', 'planning', 'ongoing', 'completed'];

  const eventImages = {
    "foot.webp": foot,
    "crick.webp": crick,
    "basket.webp": basket,
    "valley.webp": valley,
    "tenis.webp": tenis,
    "badmin.webp": badmin
  };

  useEffect(() => {
    setEvents(eventsData.events);
    setFilteredEvents(eventsData.events);
  }, []);

  useEffect(() => {
    let filtered = events;

    if (searchTerm) {
      filtered = filtered.filter(event =>
        event.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        event.venue.toLowerCase().includes(searchTerm.toLowerCase()) ||
        event.sport.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (selectedSport !== 'All') {
      filtered = filtered.filter(event => event.sport === selectedSport);
    }

    if (selectedCategory !== 'All') {
      filtered = filtered.filter(event => event.category === selectedCategory);
    }

    if (selectedStatus !== 'All') {
      filtered = filtered.filter(event => event.status === selectedStatus);
    }

    setFilteredEvents(filtered);
    setCurrentPage(1);
  }, [searchTerm, selectedSport, selectedCategory, selectedStatus, events]);

  const clearFilters = () => {
    setSearchTerm('');
    setSelectedSport('All');
    setSelectedCategory('All');
    setSelectedStatus('All');
  };

  const getStatusColor = (status) => {
    switch(status) {
      case 'upcoming': return 'status-upcoming';
      case 'planning': return 'status-planning';
      case 'ongoing': return 'status-ongoing';
      case 'completed': return 'status-completed';
      default: return '';
    }
  };

  const getStatusText = (status) => {
    return status.charAt(0).toUpperCase() + status.slice(1);
  };

  // Helper function to get booked teams count
  const getBookedCount = (bookedTeams) => {
    if (typeof bookedTeams === 'number') return bookedTeams;
    return 0;
  };

  // Helper function to check if booking is open
  const isBookingOpen = (bookedTeams) => {
    return typeof bookedTeams === 'number';
  };

  // Helper function to get slot text
  const getSlotText = (bookedTeams, maxTeams) => {
    if (typeof bookedTeams === 'string') {
      return bookedTeams;
    }
    const available = maxTeams - bookedTeams;
    if (available === 0) return '🔴 Fully Booked';
    return `${bookedTeams}/${maxTeams} teams booked • ${available} slots left`;
  };

  // Helper function to get progress percentage
  const getProgress = (bookedTeams, maxTeams) => {
    if (typeof bookedTeams !== 'number') return 0;
    return (bookedTeams / maxTeams) * 100;
  };

  // Pagination Logic
  const indexOfLastEvent = currentPage * eventsPerPage;
  const indexOfFirstEvent = indexOfLastEvent - eventsPerPage;
  const currentEvents = filteredEvents.slice(indexOfFirstEvent, indexOfLastEvent);
  const totalPages = Math.ceil(filteredEvents.length / eventsPerPage);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  // Get unique statuses from data
  const uniqueStatuses = ['All', ...new Set(eventsData.events.map(e => e.status))];

  return (
    <div className="events-page-wrapper">
      {/* ============================= */}
      {/* HERO SECTION */}
      {/* ============================= */}
      <section className="events-hero-section">
        <div className="events-hero-bg-glow events-hero-glow-1"></div>
        <div className="events-hero-bg-glow events-hero-glow-2"></div>
        <div className="events-hero-container">
          <div className="events-hero-content">
            <div className="events-hero-badge-wrapper">
              <span className="events-hero-badge-dot"></span>
              <span className="events-hero-badge">📅 All Events</span>
            </div>
            <h1 className="events-hero-title">
              Discover <span className="events-hero-highlight">Amazing</span> Sports Events
            </h1>
            <p className="events-hero-description">
              Find and register for the best sports tournaments happening near you. 
              From cricket and football to basketball and volleyball — there's 
              something for every sports enthusiast.
            </p>
            <div className="events-hero-stats">
              <div className="events-hero-stat">
                <span className="events-hero-stat-number">{eventsData.events.length}</span>
                <span className="events-hero-stat-label">Total Events</span>
              </div>
              <div className="events-hero-stat">
                <span className="events-hero-stat-number">
                  {eventsData.events.filter(e => e.status === 'upcoming').length}
                </span>
                <span className="events-hero-stat-label">Upcoming</span>
              </div>
              <div className="events-hero-stat">
                <span className="events-hero-stat-number">
                  {new Set(eventsData.events.map(e => e.sport)).size}
                </span>
                <span className="events-hero-stat-label">Sports</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============================= */}
      {/* FILTERS SECTION */}
      {/* ============================= */}
      <div className="events-container">
        <div className="events-filters-section">
          <div className="events-filter-header">
            <h2>🔍 Find Your Event</h2>
            <p>Filter events by sport, category, or status to find the perfect tournament</p>
          </div>

          <div className="events-search-bar">
            <input
              type="text"
              placeholder="Search events, sports, venues..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            {searchTerm && (
              <button className="events-clear-search" onClick={() => setSearchTerm('')}>
                ✕
              </button>
            )}
          </div>

          <div className="events-filter-options">
            <div className="events-filter-group">
              <label>🏷️ Sport</label>
              <select
                value={selectedSport}
                onChange={(e) => setSelectedSport(e.target.value)}
              >
                {sports.map(sport => (
                  <option key={sport} value={sport}>{sport}</option>
                ))}
              </select>
            </div>

            <div className="events-filter-group">
              <label>👤 Category</label>
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
              >
                {categories.map(cat => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
            </div>

            <div className="events-filter-group">
              <label>📌 Status</label>
              <select
                value={selectedStatus}
                onChange={(e) => setSelectedStatus(e.target.value)}
              >
                {uniqueStatuses.map(status => (
                  <option key={status} value={status}>
                    {status === 'All' ? 'All' : getStatusText(status)}
                  </option>
                ))}
              </select>
            </div>

            {(searchTerm || selectedSport !== 'All' || selectedCategory !== 'All' || selectedStatus !== 'All') && (
              <button className="events-clear-filters-btn" onClick={clearFilters}>
                Clear All Filters ✕
              </button>
            )}
          </div>

          <div className="events-results-count">
            Found <strong>{filteredEvents.length}</strong> event{filteredEvents.length !== 1 ? 's' : ''}
            {filteredEvents.length > 0 && ` • Page ${currentPage} of ${totalPages}`}
          </div>
        </div>

        {/* ============================= */}
        {/* EVENTS GRID */}
        {/* ============================= */}
        {filteredEvents.length > 0 ? (
          <>
            <div className="events-grid">
              {currentEvents.map(event => {
                const bookedCount = getBookedCount(event.bookedTeams);
                const bookingOpen = isBookingOpen(event.bookedTeams);
                const progress = getProgress(event.bookedTeams, event.maxTeams);
                const isFull = bookingOpen && bookedCount === event.maxTeams;
                const slotText = getSlotText(event.bookedTeams, event.maxTeams);

                return (
                  <div key={event.id} className="events-card">
                    <div className="events-card-image">
                      <img 
                        src={eventImages[event.image]} 
                        alt={event.title}
                        loading="lazy" // ✅ Lazy Loading Added
                        onError={(e) => {
                          e.target.src = 'https://via.placeholder.com/600x400?text=Sports+Event';
                        }}
                      />
                      <span className={`events-status-badge ${getStatusColor(event.status)}`}>
                        {getStatusText(event.status)}
                      </span>
                      {isFull && (
                        <span className="events-full-badge">🔴 Full</span>
                      )}
                      <span className="events-sport-tag">{event.icon} {event.sport}</span>
                    </div>

                    <div className="events-card-body">
                      <div className="events-card-header">
                        <h3 className="events-card-title">{event.title}</h3>
                        <span className="events-card-fee">{event.fee}</span>
                      </div>
                      
                      <p className="events-card-description">
                        {event.description.length > 120 
                          ? event.description.substring(0, 120) + '...' 
                          : event.description}
                      </p>

                      <div className="events-card-meta">
                        <div className="events-meta-item">
                          <span>📅</span>
                          <span>{event.date}</span>
                        </div>
                        <div className="events-meta-item">
                          <span>⏰</span>
                          <span>{event.time}</span>
                        </div>
                        <div className="events-meta-item">
                          <span>📍</span>
                          <span>{event.venue}</span>
                        </div>
                        <div className="events-meta-item">
                          <span>👥</span>
                          <span>{event.category}</span>
                        </div>
                      </div>

                      <div className="events-card-slots">
                        <div className="events-slot-progress">
                          <div 
                            className="events-slot-progress-bar" 
                            style={{ width: `${Math.min(progress, 100)}%` }}
                          ></div>
                        </div>
                        <span className="events-slot-text">{slotText}</span>
                      </div>

                      <Link to={`/events/${event.id}`} className="events-detail-btn">
                        View Details →
                      </Link>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* ============================= */}
            {/* PAGINATION */}
            {/* ============================= */}
            {totalPages > 1 && (
              <div className="events-pagination">
                <button 
                  className="events-pagination-btn"
                  onClick={() => paginate(currentPage - 1)}
                  disabled={currentPage === 1}
                >
                  ← Previous
                </button>
                <div className="events-pagination-numbers">
                  {[...Array(totalPages).keys()].map(number => (
                    <button
                      key={number + 1}
                      className={`events-pagination-number ${currentPage === number + 1 ? 'active' : ''}`}
                      onClick={() => paginate(number + 1)}
                    >
                      {number + 1}
                    </button>
                  ))}
                </div>
                <button 
                  className="events-pagination-btn"
                  onClick={() => paginate(currentPage + 1)}
                  disabled={currentPage === totalPages}
                >
                  Next →
                </button>
              </div>
            )}
          </>
        ) : (
          <div className="events-no-results">
            <span className="events-no-results-icon">🔍</span>
            <h3>No Events Found</h3>
            <p>Try adjusting your filters or search terms</p>
            <button className="events-clear-filters-btn" onClick={clearFilters}>
              Clear All Filters
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default Events;